import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FiSave, FiMessageSquare, FiLayout } from 'react-icons/fi';
import companyService from '../../../../services/company.service';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';
import AppTour from '../../../../components/common/AppTour';
import QuotationDesigner from './QuotationDesigner';

const CotizaPlusSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [company, setCompany] = useState(null);
    const [activeTab, setActiveTab] = useState('messaging'); // 'messaging' or 'designer'
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);

    const { register, handleSubmit, setValue, getValues } = useForm();

    const insertTag = (fieldName, tag) => {
        const textarea = document.querySelector(`textarea[name="${fieldName}"]`);

        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value;
            const before = text.substring(0, start);
            const after = text.substring(end, text.length);
            const newValue = before + tag + after;

            setValue(fieldName, newValue, { shouldValidate: true, shouldDirty: true });

            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + tag.length, start + tag.length);
            }, 0);
        } else {
            const current = getValues(fieldName) || '';
            setValue(fieldName, current + tag, { shouldValidate: true, shouldDirty: true });
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'configuracion_modulo') {
            setRunTour(true);
        }
        if (params.get('tab')) {
            setActiveTab(params.get('tab'));
        }
        loadCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const loadCompany = async () => {
        try {
            setLoading(true);
            const data = await companyService.getMyCompany();
            setCompany(data);

            setValue('mensaje_autoatencion', data.mensaje_autoatencion || '');
            setValue('autoaprobar_cotizaciones', data.autoaprobar_cotizaciones || false);
            setValue('mensaje_correo_cotizacion', data.mensaje_correo_cotizacion || '');
            setValue('mensaje_whatsapp_cotizacion', data.mensaje_whatsapp_cotizacion || '');
        } catch (err) {
            console.error('Error loading company:', err);
            Swal.fire('Error', 'Error al cargar configuración.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('mensaje_autoatencion', data.mensaje_autoatencion);
            formData.append('autoaprobar_cotizaciones', data.autoaprobar_cotizaciones);
            formData.append('mensaje_correo_cotizacion', data.mensaje_correo_cotizacion);
            formData.append('mensaje_whatsapp_cotizacion', data.mensaje_whatsapp_cotizacion);

            formData.append('nombre', company.nombre);
            formData.append('rut', company.rut);
            formData.append('direccion', company.direccion);
            formData.append('telefono', company.telefono);
            formData.append('email', company.email);

            await companyService.update(company.id, formData);

            Swal.fire({
                icon: 'success',
                title: 'Guardado',
                text: 'Configuración guardada correctamente.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err) {
            console.error('Error updating company:', err);
            Swal.fire('Error', 'Error al guardar cambios.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const configuradorTourSteps = [
        {
            element: '#tour-cotizaplus-settings-header',
            intro: '¡Ajustes Globales del Módulo! Aquí puedes personalizar cómo te comunicas con tus clientes de forma automática.',
            position: 'bottom',
        },
        {
            element: '#tour-cotizaplus-settings-portal',
            intro: '🌐 Redacta aquí el mensaje de bienvenida que leerán tus clientes cuando entren a tu Enlace Público (Link de Autoatención).',
            position: 'top',
        },
        {
            element: '#tour-cotizaplus-settings-plantillas',
            intro: '✉️ Puedes crear plantillas personalizadas para los correos y WhatsApp utilizando los botones azules y verdes para inyectar variables mágicas como el nombre del cliente o el monto total.',
            position: 'top',
        },
        {
            element: '#tour-cotizaplus-settings-autoaprobar',
            intro: '⚖️ Si activas esto, las cotizaciones que los clientes hagan por el portal público pasarán directamente al estado ENVIADA en vez de quedarse como BORRADOR (ahorrando tiempo si no necesitas revisarlas).',
            position: 'top',
        }
    ];

    if (loading) return <div className="loading-overlay"><div className="spinner"></div></div>;

    return (
        <div className="container-custom">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={configuradorTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Link Público' }}
                    onTourComplete={() => {
                        window.location.href = '/app/cotizaplus/autoatencion?tour=autoatencion';
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-cotizaplus-settings-header">
                <div>
                    <h2>Configuración de CotizaPlus</h2>
                    <p className="text-muted">Personaliza los mensajes y flujos de tus cotizaciones e inventario</p>
                </div>
            </div>

            <div className="settings-tabs">
                <div
                    className={`settings-tab ${activeTab === 'messaging' ? 'active' : ''}`}
                    onClick={() => setActiveTab('messaging')}
                >
                    <FiMessageSquare style={{ marginRight: '8px' }} />
                    Mensajería y Flujos
                </div>
                <div
                    className={`settings-tab ${activeTab === 'designer' ? 'active' : ''}`}
                    onClick={() => setActiveTab('designer')}
                >
                    <FiLayout style={{ marginRight: '8px' }} />
                    Diseño de Cotización
                </div>
            </div>

            {activeTab === 'messaging' ? (
                <form onSubmit={handleSubmit(onSubmit)} className="card-custom" style={{ maxWidth: '800px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                            <FiMessageSquare /> Mensajería y Flujos
                        </h3>
                        <button type="submit" className="btn-custom btn-primary d-none d-lg-flex" disabled={saving}>
                            <FiSave size={18} style={{ marginRight: '8px' }} />
                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>

                    <div className="form-group-custom" style={{ marginTop: '20px' }} id="tour-cotizaplus-settings-portal">
                        <div style={{ padding: '15px', backgroundColor: 'var(--blue-50)', borderRadius: '8px', marginBottom: '10px' }}>
                            <label className="form-label-custom" style={{ color: 'var(--blue-800)' }}>Portal de Autoatención</label>
                            <p className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                                Mensaje de Bienvenida que aparecerá en la parte superior del formulario público de cotización.
                            </p>
                            <textarea
                                {...register('mensaje_autoatencion')}
                                className="form-control-custom"
                                rows="3"
                                placeholder="Ej: Bienvenido a nuestro portal de cotizaciones..."
                                style={{ backgroundColor: 'white' }}
                            />
                        </div>
                    </div>

                    <div className="form-group-custom" style={{ marginTop: '20px' }} id="tour-cotizaplus-settings-plantillas">
                        <h4 style={{ fontSize: '16px', marginBottom: '15px' }}>Plantillas de Correo Electrónico</h4>
                        <textarea
                            {...register('mensaje_correo_cotizacion')}
                            className="form-control-custom"
                            rows="4"
                            placeholder="Ej: Estimado {cliente_nombre}, adjunto su cotización #{cotizacion_numero}..."
                        />
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {[
                                { tag: '{cliente_nombre}', label: 'Nombre Cliente' },
                                { tag: '{cotizacion_numero}', label: 'N° Cotización' },
                                { tag: '{empresa_nombre}', label: 'Nombre Empresa' },
                                { tag: '{total}', label: 'Monto Total' },
                                { tag: '{empresa_logo}', label: 'Logo Empresa' }
                            ].map(item => (
                                <button
                                    key={item.tag}
                                    type="button"
                                    onClick={() => insertTag('mensaje_correo_cotizacion', item.tag)}
                                    className="badge"
                                    style={{
                                        border: 'none', cursor: 'pointer', padding: '8px 12px',
                                        fontSize: '12px', backgroundColor: 'var(--info-blue)',
                                        color: 'var(--white)', borderRadius: '20px',
                                        display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '500'
                                    }}
                                >
                                    + {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="form-group-custom" style={{ marginTop: '20px' }}>
                        <h4 style={{ fontSize: '16px', marginBottom: '15px' }}>Plantillas de WhatsApp</h4>
                        <textarea
                            {...register('mensaje_whatsapp_cotizacion')}
                            className="form-control-custom"
                            rows="3"
                            placeholder="Ej: Hola {cliente_nombre}, aquí tienes tu cotización #{cotizacion_numero}..."
                        />
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {[
                                { tag: '{cliente_nombre}', label: 'Nombre Cliente' },
                                { tag: '{cotizacion_numero}', label: 'N° Cotización' },
                                { tag: '{empresa_nombre}', label: 'Nombre Empresa' }
                            ].map(item => (
                                <button
                                    key={item.tag}
                                    type="button"
                                    onClick={() => insertTag('mensaje_whatsapp_cotizacion', item.tag)}
                                    className="badge"
                                    style={{
                                        border: 'none', cursor: 'pointer', padding: '8px 12px',
                                        fontSize: '12px', backgroundColor: 'var(--success-green)',
                                        color: '#fff', borderRadius: '20px',
                                        display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '500'
                                    }}
                                >
                                    + {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="form-group-custom" style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }} id="tour-cotizaplus-settings-autoaprobar">
                        <label className="form-label-custom" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: 0 }}>
                            <input
                                type="checkbox"
                                {...register('autoaprobar_cotizaciones')}
                                style={{ width: '18px', height: '18px', accentColor: 'var(--primary-orange)' }}
                            />
                            <span>Habilitar Auto-aprobación de Cotizaciones</span>
                        </label>
                        <p className="text-muted" style={{ fontSize: '12px', marginTop: '5px', marginLeft: '28px' }}>
                            Si se activa, las cotizaciones recibidas por el link de autoatención se marcarán automáticamente como <strong>ENVIADA</strong> en lugar de BORRADOR.
                        </p>
                    </div>

                    {/* FAB Guardar para Móviles */}
                    <button
                        type="submit"
                        className="fab-custom d-lg-none"
                        disabled={saving}
                        title="Guardar Cambios"
                        style={{ background: 'linear-gradient(135deg, var(--primary-orange), var(--accent-coral))' }}
                    >
                        <FiSave size={24} />
                    </button>
                </form>
            ) : (
                <QuotationDesigner />
            )}
        </div>
    );
};

export default CotizaPlusSettings;
